import { z } from 'zod'

/**
 * Un schema que describe la FORMA de una sección del expediente sin exigir que
 * esté llena. La contracara de lectura de `openShape`.
 *
 * ── POR QUÉ EXISTE ───────────────────────────────────────────────────────────
 *
 * Las secciones del FEIC —solicitante, PEP, económica, patrimonial,
 * representantes— están guardadas como `Mixed`: **la base no tiene contrato**. El
 * SSOT las valida al ESCRIBIR, y ahí sus reglas son correctas: `requiredString()`
 * exige que el asesor complete el campo, `requiredMoney()` que la fila tenga
 * importe. Son reglas del SUBMIT de un formulario.
 *
 * Al LEER, la API no construye estas secciones: las REENVÍA tal cual salieron de
 * Mongo. Heredar las reglas del submit en el contrato de lectura significa exigir
 * lo que nadie garantizó nunca — y lo que el registro efectivamente no tiene.
 * Producción lo demuestra con tres expedientes, cada uno roto de una forma
 * distinta:
 *
 * | Guardado                        | Regla heredada        | Qué pasaba          |
 * |---------------------------------|-----------------------|---------------------|
 * | `currency` AUSENTE              | `requiredString()`    | el 200 se descarta  |
 * | `quantity: null` (fideicomiso)  | `requiredNumeric()`   | el 200 se descarta  |
 * | `businessRelationship: ''`      | `.min(1)`             | el 200 se descarta  |
 *
 * Los tres se veían igual: «ocurrió un error» en el panel, y NADA en ningún log
 * —ni en la API, que había respondido 200, ni en Sentry, que no ve los errores de
 * query—. Un dato pobre tumbaba la pantalla entera y en silencio.
 *
 * ── QUÉ RELAJA, Y QUÉ NO ─────────────────────────────────────────────────────
 *
 * Relaja **la pobreza del dato**: ausencia, `null`, cadena vacía, un número
 * negativo que alguien tecleó, un valor fuera de un catálogo que nunca se
 * impuso al escribir.
 *
 * NO relaja **la forma**. Un objeto sigue teniendo que ser un objeto y un array
 * un array. Si el mapper de la API se rompe y manda un número donde va una
 * sección, esto SIGUE fallando — que es exactamente lo que un portero de
 * respuestas tiene que atrapar (ver `assert-shape.ts` en la web). La diferencia
 * es que ahora falla por corrupción de la forma y no por un expediente a medio
 * llenar.
 *
 * Por eso no alcanzaba con `.partial()`: hace opcional el primer nivel y nada
 * más. `undefined` no es `null`, y ninguno de los dos es `''`.
 *
 * ── SOBRE LOS CATÁLOGOS ──────────────────────────────────────────────────────
 *
 * Los enums se abren a `string`. No es descuido: un contrato de lectura sobre
 * datos sin contrato en la base no puede prometer un catálogo cerrado. El campo
 * «Moneda» del FEIC interno es un `<input>` libre con un `<datalist>` de
 * sugerencias —el datalist SUGIERE, no restringe—, y así fue como entró un
 * «DIRHAM» que ninguna versión del código ofreció jamás. Cerrar esos campos es
 * trabajo de la ESCRITURA; mientras tanto, la lectura no puede mentir sobre lo
 * que hay guardado.
 *
 * ── CUÁNDO SE BORRA ──────────────────────────────────────────────────────────
 *
 * Cuando las secciones dejen de ser `Mixed` y la base tenga su propio contrato.
 * Ahí el dato guardado sí estará garantizado y esta tolerancia pasará a esconder
 * bugs en vez de tolerar historia. Ver `docs/architecture/forms-unification-strategy.md`.
 */
export function storedShape<T extends z.ZodTypeAny>(schema: T): T {
  return relax(schema) as unknown as T
}

function relax(schema: z.ZodTypeAny): z.ZodTypeAny {
  const def = schema._def as { typeName: string } & Record<string, never>

  switch (def.typeName) {
    /*
      Cada propiedad se afloja Y se hace `nullish`: el registro guarda tanto la
      AUSENCIA de la clave como un `null` explícito, y para Zod no son lo mismo.
      `passthrough()` conserva lo que el otro vocabulario nombró distinto, igual
      que `openShape` — perder eso en una lectura sería devolver un expediente
      mutilado.
    */
    case z.ZodFirstPartyTypeKind.ZodObject: {
      const object = schema as unknown as z.ZodObject<z.ZodRawShape>
      const shape = Object.fromEntries(
        Object.entries(object.shape).map(([key, value]) => [key, relax(value).nullish()])
      )
      return z.object(shape).passthrough()
    }

    /*
      Se DESCARTAN el `min`/`max` del array, al revés que `openShape`. Allá un
      `min(1)` —«al menos una nacionalidad»— es una validación de verdad que hay
      que conservar al escribir. Acá sería exigirle a un expediente a medio
      llenar que ya esté completo, y tumbar su pantalla por no estarlo.
    */
    case z.ZodFirstPartyTypeKind.ZodArray: {
      const array = schema as unknown as z.ZodArray<z.ZodTypeAny>
      return z.array(relax(array.element))
    }

    case z.ZodFirstPartyTypeKind.ZodOptional:
      return relax((schema as unknown as z.ZodOptional<z.ZodTypeAny>).unwrap()).nullish()

    case z.ZodFirstPartyTypeKind.ZodNullable:
      return relax((schema as unknown as z.ZodNullable<z.ZodTypeAny>).unwrap()).nullish()

    /*
      El default se DESCARTA. Un default en una lectura inventa un dato que el
      registro no tiene: `banks: []` diría «este cliente no declaró cuentas»
      cuando lo cierto es que no sabemos. La ausencia se devuelve como ausencia.
    */
    case z.ZodFirstPartyTypeKind.ZodDefault:
      return relax((schema as unknown as z.ZodDefault<z.ZodTypeAny>).removeDefault()).nullish()

    /*
      El caso que hace falta para los importes. `requiredMoney()` es un
      `preprocess` que normaliza `''`/`null` a `undefined` y después exige un
      número: aflojar por AFUERA no alcanza, porque el `null` nunca llega al
      envoltorio —lo consume el preprocess y lo convierte en el `undefined` que
      el número de adentro rechaza—. Hay que aflojar el schema DESTINO.
    */
    case z.ZodFirstPartyTypeKind.ZodEffects: {
      const effects = schema as unknown as z.ZodEffects<z.ZodTypeAny>
      const inner = relax(effects.innerType()).nullish()

      /*
        Un REFINAMIENTO se tira; una TRANSFORMACIÓN se conserva. Son dos cosas distintas metidas
        en el mismo envoltorio de zod, y tratarlas igual rompe de un lado o del otro.

        Un `refine`/`superRefine` es una regla de SUBMIT como cualquier otra —el dígito
        verificador del CUI, un par de campos que se exigen entre sí— y en una lectura exige lo
        que nadie garantizó. Es lo que tumbaba la revisión de un cliente que había elegido «DPI»
        y todavía no tecleaba el número: el 200 traía el expediente entero y el portero lo
        descartaba por «El número de CUI/DPI no es válido», sobre un campo a medio escribir.

        Un `transform`/`preprocess` NO valida: normaliza. `requiredMoney()` convierte `''`/`null`
        en `undefined` antes de mirar el número, y sin él lo que llega del registro ni siquiera
        parsea. Ése es el caso que hizo nacer esta rama del switch.
      */
      if (effects._def.effect.type === 'refinement') return inner

      return new z.ZodEffects({ ...effects._def, schema: inner })
    }

    case z.ZodFirstPartyTypeKind.ZodUnion: {
      const union = schema as unknown as z.ZodUnion<[z.ZodTypeAny, ...z.ZodTypeAny[]]>
      const options = union.options.map((option) => relax(option))
      return z.union(options as [z.ZodTypeAny, z.ZodTypeAny, ...z.ZodTypeAny[]])
    }

    // Se conserva el TIPO y se tiran los refinamientos de valor: `.min(1)`,
    // `.email()`, `.nonnegative()`, `.int()`. Todos son reglas de submit.
    case z.ZodFirstPartyTypeKind.ZodString:
      return z.string()
    /*
      `int` se CONSERVA. `min`, `max` y `nonnegative` son reglas de VALOR y se
      van; `int` no describe un valor aceptable sino el tipo, y viaja al JSON
      Schema como `integer`. Tirarlo ensanchaba 120 propiedades del contrato
      publicado de `integer` a `number` —un cambio incompatible de verdad— sin
      tener nada que ver con lo que había que arreglar.
    */
    case z.ZodFirstPartyTypeKind.ZodNumber: {
      const isInt = (schema as unknown as z.ZodNumber)._def.checks.some(
        (check) => check.kind === 'int'
      )
      return isInt ? z.number().int() : z.number()
    }

    // Ver «SOBRE LOS CATÁLOGOS» arriba.
    case z.ZodFirstPartyTypeKind.ZodEnum:
      return z.string()

    default:
      return schema
  }
}
